/**
 * Cross-protocol feed service - merges AT Protocol, Mastodon, and Pixelfed timelines
 */
import { Agent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import type { MastodonSession } from '@/lib/mastodon'
import type { PixelfedSession } from '@/lib/pixelfed'
import type { Post, PostsPage } from '@/types/posts'
import { fetchTimeline } from '@/services/postsService'
import { fetchMastodonTimeline } from '@/services/mastodonService'
import { fetchPixelfedTimeline } from '@/services/pixelfedService'

export interface CrossProtocolCursors {
    bluesky?: string | null
    mastodon?: string | null
    pixelfed?: string | null
}

/**
 * Fetch Bluesky, Mastodon, and optionally Pixelfed timelines concurrently
 * and merge them chronologically.
 *
 * Uses Promise.allSettled so one protocol failing doesn't block the others.
 * Deduplicates bridged posts (Bridgy Fed) by keeping the native ActivityPub version.
 *
 * Cursor convention: undefined = first page, null = exhausted (skip fetching).
 */
export async function fetchCrossProtocolTimeline(
    session: OAuthSession,
    mastodonSession: MastodonSession | null,
    options?: { cursors?: CrossProtocolCursors; pixelfedSession?: PixelfedSession }
): Promise<PostsPage> {
    const blueskyExhausted = options?.cursors?.bluesky === null
    const mastodonExhausted = options?.cursors?.mastodon === null
    const pixelfedExhausted = options?.cursors?.pixelfed === null

    // fetchTimeline now takes an Agent directly. The cross-protocol timeline uses the
    // default (Bluesky) AppView — no proxy. AppView-aware routing happens in useTimeline
    // via the adapter path; useCrossProtocolTimeline is separate and Bluesky-default.
    const blueskyPromise = blueskyExhausted
        ? Promise.resolve({ posts: [], cursor: undefined } as PostsPage)
        : fetchTimeline(new Agent(session), {
              cursor: options?.cursors?.bluesky ?? undefined,
          })

    const mastodonPromise =
        !mastodonSession || mastodonExhausted
            ? Promise.resolve({ posts: [], cursor: undefined } as PostsPage)
            : fetchMastodonTimeline(mastodonSession, {
                  cursor: options?.cursors?.mastodon ?? undefined,
              })

    const pixelfedPromise =
        !options?.pixelfedSession || pixelfedExhausted
            ? Promise.resolve({ posts: [], cursor: undefined } as PostsPage)
            : fetchPixelfedTimeline(options.pixelfedSession, {
                  cursor: options?.cursors?.pixelfed ?? undefined,
              })

    const [blueskyResult, mastodonResult, pixelfedResult] = await Promise.allSettled([
        blueskyPromise,
        mastodonPromise,
        pixelfedPromise,
    ])

    const blueskyPage = blueskyResult.status === 'fulfilled' ? blueskyResult.value : null
    const mastodonPage = mastodonResult.status === 'fulfilled' ? mastodonResult.value : null
    const pixelfedPage = pixelfedResult.status === 'fulfilled' ? pixelfedResult.value : null

    if (!blueskyPage && !mastodonPage && !pixelfedPage) {
        const errors = [
            blueskyResult.status === 'rejected' ? blueskyResult.reason : null,
            mastodonResult.status === 'rejected' ? mastodonResult.reason : null,
            pixelfedResult.status === 'rejected' ? pixelfedResult.reason : null,
        ].filter(Boolean)
        throw new Error(
            `All protocols failed: ${errors.map(e => String(e?.message ?? e)).join('; ')}`
        )
    }

    const blueskyPosts = blueskyPage?.posts ?? []
    const mastodonPosts = mastodonPage?.posts ?? []
    const pixelfedPosts = pixelfedPage?.posts ?? []

    const merged = deduplicateBridgedPosts([...blueskyPosts, ...mastodonPosts, ...pixelfedPosts])
    merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Compute next cursors:
    // - Exhausted protocols stay null
    // - Successful fetches use the returned cursor (undefined = exhausted this page)
    // - Failed protocols carry forward their previous cursor so they retry on next page
    const nextBluesky = blueskyExhausted
        ? null
        : blueskyPage
          ? (blueskyPage.cursor ?? null)
          : (options?.cursors?.bluesky ?? null)
    const nextMastodon = mastodonExhausted
        ? null
        : mastodonPage
          ? (mastodonPage.cursor ?? null)
          : (options?.cursors?.mastodon ?? null)
    const nextPixelfed = pixelfedExhausted
        ? null
        : pixelfedPage
          ? (pixelfedPage.cursor ?? null)
          : (options?.cursors?.pixelfed ?? null)

    return {
        posts: merged,
        cursor: nextBluesky ?? undefined,
        mastodonCursor: nextMastodon ?? undefined,
        pixelfedCursor: nextPixelfed ?? undefined,
    }
}

/**
 * Deduplicate bridged posts: if a Bluesky post has a bridgyOriginalUrl that matches
 * a Mastodon post's URI, keep only the native Mastodon version.
 */
export function deduplicateBridgedPosts(posts: Post[]): Post[] {
    // Collect all Mastodon post URIs for quick lookup
    const mastodonUris = new Set<string>()
    for (const post of posts) {
        if (post.isActivityPub) {
            mastodonUris.add(post.uri)
        }
    }

    return posts.filter(post => {
        // If this is a Bluesky post bridged from Mastodon, and we have the native version, skip it
        if (
            !post.isActivityPub &&
            post.bridgyOriginalUrl &&
            mastodonUris.has(post.bridgyOriginalUrl)
        ) {
            return false
        }
        return true
    })
}
